import React, { Ref } from "react";

import { DataGrid, DataGridProps, gridClasses } from "@mui/x-data-grid";
import { styled, alpha } from "@mui/material";

/*
  This implementation is based off the MUI example for Striped rows
  https://mui.com/x/react-data-grid/style/#striped-rows
*/

const ODD_OPACITY = 0.2;

const StripedDataGridStyle = styled(DataGrid)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[200],
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
    '&.Mui-selected': {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity,
      ),
      '&:hover': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
            theme.palette.action.selectedOpacity +
            theme.palette.action.hoverOpacity,
        ),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity,
          ),
        },
      },
    },
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[800],
    }),
  },
}));

interface StripedDataGridProps extends DataGridProps {
  ref?: Ref<HTMLDivElement> | undefined;
  /**
   * Determines whether or not the DataGrid rows should be striped.
   * @default true
   */
  showRowStripes?: boolean;
}

export default function StripedDataGrid({ref, showRowStripes, ...props}: StripedDataGridProps): React.JSX.Element {
  const showStripes = showRowStripes ?? true;
  return (
    <StripedDataGridStyle
      ref={ref}
      getRowClassName={(params) => {
        return showStripes 
          ? params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          : "";
      }}
      {...props}
    />
  );
}